import { Children, cloneElement, isValidElement, useState, type ReactElement, type ReactNode } from 'react';
import { EXPORT_ROOT_ATTR, findChartData, flattenRow, type CsvRow } from '../exportUtils';
import { ExportMenu } from './ExportMenu';
import { InfoTooltip } from './InfoTooltip';

const RECHARTS_CHARTS = new Set(['LineChart', 'BarChart', 'AreaChart', 'ComposedChart', 'PieChart', 'ScatterChart', 'RadarChart', 'RadialBarChart']);

/** Turns on Recharts' keyboard layer: focus the chart, then arrow keys step through the data points. */
function withKeyboardNavigation(node: ReactNode, depth = 0): ReactNode {
  if (depth > 6) return node;
  // Arrays are mapped with keys kept; a single child stays single, since ResponsiveContainer expects one element.
  if (Array.isArray(node)) return Children.map(node, (child) => withKeyboardNavigation(child, depth));
  if (!isValidElement(node)) return node;
  const element = node as ReactElement<{ children?: ReactNode; accessibilityLayer?: boolean }>;
  const name = (element.type as { displayName?: string }).displayName;
  if (name && RECHARTS_CHARTS.has(name)) return cloneElement(element, { accessibilityLayer: true });
  const inner = element.props.children;
  if (inner === undefined || typeof inner === 'function') return element;
  return cloneElement(element, undefined, withKeyboardNavigation(inner, depth + 1));
}

function formatCell(value: string | number | boolean | null): string {
  if (value === null) return '—';
  if (typeof value === 'number') return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return String(value);
}

function DataTable({ title, rows }: { title: string; rows: CsvRow[] | null }) {
  const flat = (rows ?? []).map(flattenRow);
  const columns = [...new Set(flat.flatMap((r) => Object.keys(r)))];
  if (flat.length === 0) return <p className="text-sm text-text-muted">No data to show.</p>;
  return (
    <div className="h-full overflow-auto" tabIndex={0} role="region" aria-label={`${title} data`}>
      <table className="w-full text-left text-xs">
        <caption className="sr-only">{title}</caption>
        <thead className="sticky top-0 border-b border-border bg-surface text-[11px] uppercase tracking-wide text-text-faint">
          <tr>
            {columns.map((c) => (
              <th key={c} scope="col" className="pb-2 pr-3">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {flat.map((row, i) => (
            <tr key={i} className="border-b border-border last:border-0">
              {columns.map((c) => (
                <td key={c} className={`py-1.5 pr-3 ${typeof row[c] === 'number' ? 'text-right tabular-nums' : 'text-text-default'}`}>
                  {formatCell(row[c] ?? null)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ChartCard({
  title,
  caption,
  info,
  actions,
  height = 'h-80',
  scroll,
  className = '',
  csvRows,
  children,
}: {
  title: string;
  caption?: string;
  info?: string;
  actions?: ReactNode;
  height?: string;
  scroll?: boolean;
  className?: string;
  /** CSV rows for the download; by default the data the chart renders (see findChartData), else the card's table. */
  csvRows?: CsvRow[];
  children: ReactNode;
}) {
  const [asTable, setAsTable] = useState(false);
  const rows = () => csvRows ?? findChartData(children);
  return (
    <div {...{ [EXPORT_ROOT_ATTR]: '' }} className={`flex flex-col rounded-card border border-border bg-surface p-5 shadow-sm ${height} ${className}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="flex items-center gap-1.5 text-sm font-medium text-text-default">
            {title}
            {info && <InfoTooltip text={info} />}
          </h3>
          {caption && <p className="mt-1 text-xs text-text-muted">{caption}</p>}
        </div>
        <div className="-mr-2 -mt-2 flex shrink-0 items-start">
          {actions && <div {...{ 'data-export-ignore': '' }}>{actions}</div>}
          <button
            type="button"
            {...{ 'data-export-ignore': '' }}
            onClick={() => setAsTable((v) => !v)}
            aria-pressed={asTable}
            aria-label={asTable ? `Show ${title} as a chart` : `Show ${title} as a table`}
            title={asTable ? 'Show as chart' : 'Show data as table'}
            className="flex h-11 w-11 items-center justify-center rounded-input text-text-muted transition-colors duration-150 hover:bg-surface-alt hover:text-text-default focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue"
          >
            <i aria-hidden="true" className={`fa-solid ${asTable ? 'fa-chart-column' : 'fa-table'} text-sm`} />
          </button>
          <ExportMenu title={title} getRows={rows} />
        </div>
      </div>
      <div className={`mt-4 min-h-0 flex-1 ${scroll || asTable ? 'overflow-y-auto' : ''}`}>
        {asTable ? (
          <DataTable title={title} rows={rows()} />
        ) : (
          <div role="group" aria-label={`${title} chart. Focus the chart and use the arrow keys to step through the values, or show it as a table.`} className="h-full">
            {withKeyboardNavigation(children)}
          </div>
        )}
      </div>
    </div>
  );
}
